import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Input,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  Textarea,
  useDisclosure
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { Icon } from '../../../components/Icon'
import { LoadingBlock } from '../../../components/Loading'
import { ApiErrorShape, toFormErrors, useAppToast } from '../../../helpers'
import ContestsService from '../../../services/Contests/ContestsService'
import { IExhibitorCertificatesStats } from '../../../services/Contests/interfaces'

interface IContestsSendExhibitorCertificatesModalProps {
  isOpen: boolean
  onClose: () => void
  contestId: number
  contestName: string
}

const DEFAULT_SUBJECT = 'Certificado de Expositor — #NOME_EVENTO'

const DEFAULT_BODY = `Olá #PRIMEIRO_NOME,

Segue o certificado por ter participado da exposição #NOME_EVENTO.

Atenciosamente,
ABRA - Academia Brasileira de Arte`

const EMPTY_ERRORS = { subject: '', body: '', certificate_date: '' }

// Today in the browser's timezone — `toISOString()` would roll over to tomorrow
// after 21h in Brasília.
const today = (): string => {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')

  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

export default function ContestsSendExhibitorCertificatesModal({
  isOpen,
  onClose,
  contestId,
  contestName
}: IContestsSendExhibitorCertificatesModalProps) {
  const toast = useAppToast()
  const contestService = useMemo(() => new ContestsService(), [])
  const [stats, setStats] = useState<IExhibitorCertificatesStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [force, setForce] = useState(false)
  const [error, setError] = useState(false)
  const [certificateDate, setCertificateDate] = useState(today)
  const [subject, setSubject] = useState(DEFAULT_SUBJECT)
  const [body, setBody] = useState(DEFAULT_BODY)
  const [formErrors, setFormErrors] = useState(EMPTY_ERRORS)
  const confirmDialog = useDisclosure()

  // Reloads the preview; a cancellation flag drops a stale response when `force`
  // is toggled twice quickly, so the last click always wins.
  const loadPreview = useCallback(() => {
    if (!contestId) return undefined

    let active = true
    setError(false)
    setLoading(true)
    contestService
      .getExhibitorCertificatesPreview(contestId, force)
      .then((data) => {
        if (active) setStats(data)
      })
      .catch(() => {
        if (!active) return
        setError(true)
        setStats(null)
        toast.error('Erro ao carregar os destinatários!')
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [contestId, force, contestService, toast])

  useEffect(() => {
    if (!isOpen) return
    return loadPreview()
  }, [isOpen, loadPreview])

  const handleClose = () => {
    setForce(false)
    setStats(null)
    setError(false)
    setCertificateDate(today())
    setSubject(DEFAULT_SUBJECT)
    setBody(DEFAULT_BODY)
    setFormErrors(EMPTY_ERRORS)
    onClose()
  }

  const handlePreviewPdf = async () => {
    setPreviewing(true)

    try {
      await contestService.openExhibitorCertificatePreviewPdf(contestId, certificateDate)
    } catch (err) {
      toast.error('Erro ao gerar a pré-visualização do certificado!', err)
    } finally {
      setPreviewing(false)
    }
  }

  const handleSend = async () => {
    setSending(true)
    setFormErrors(EMPTY_ERRORS)

    try {
      const result = await contestService.sendExhibitorCertificates(contestId, {
        subject,
        body,
        certificate_date: certificateDate,
        force
      })

      toast.success(
        'Envio de certificados agendado!',
        `${result.recipients} participante(s) vão receber o certificado de expositor por e-mail.`
      )

      confirmDialog.onClose()
      handleClose()
    } catch (err) {
      confirmDialog.onClose()
      setFormErrors(toFormErrors(EMPTY_ERRORS, (err as ApiErrorShape).response?.data?.errors))
      toast.error('Erro ao enviar os certificados!', err)
    } finally {
      setSending(false)
    }
  }

  const isFormFilled = subject.trim() !== '' && body.trim() !== '' && certificateDate !== ''
  const canSend = !!stats && stats.recipients > 0 && isFormFilled && !loading && !sending
  const canPreview = !!stats && stats.participants > 0 && certificateDate !== '' && !previewing

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={handleClose}
        title="Enviar certificados de expositor"
        size="wide"
        footer={
          // The footer has three controls: the resend option on the left, the two
          // actions grouped on the right — ModalFooter puts no gap between children.
          <Flex w="100%" align="center" gap={3} wrap="wrap">
            <Checkbox mr="auto" isChecked={force} onChange={(e) => setForce(e.target.checked)} isDisabled={sending}>
              <Text fontSize="sm">Reenviar para quem já recebeu</Text>
            </Checkbox>

            <Button
              variant="outline"
              type="button"
              onClick={handlePreviewPdf}
              isLoading={previewing}
              loadingText="Gerando..."
              isDisabled={!canPreview}
            >
              <Icon name="lni lni-eye" mr="6px" />
              Pré-visualizar certificado
            </Button>

            <Button
              variant="magenta"
              type="button"
              onClick={confirmDialog.onOpen}
              isLoading={sending}
              loadingText="Enviando..."
              isDisabled={!canSend}
            >
              Enviar certificados
            </Button>
          </Flex>
        }
      >
        <Text variant="caption" mb={4}>
          Envia por e-mail o certificado de expositor, em PDF, a cada participante com obra exposta (inscrição paga e
          obra aprovada para exposição) — <strong>{contestName}</strong>.
        </Text>

        {loading && <LoadingBlock />}

        {!loading && error && (
          <Alert status="error" borderRadius="md" alignItems="center">
            <AlertIcon />
            <Text fontSize="sm">Não foi possível carregar os destinatários.</Text>
            <Button size="sm" ml="auto" onClick={loadPreview}>
              Tentar novamente
            </Button>
          </Alert>
        )}

        {!loading && stats && (
          <>
            <Flex gap={4} wrap="wrap">
              <Stat minW="120px">
                <StatLabel fontSize="xs">Participantes com obra exposta</StatLabel>
                <StatNumber fontSize="2xl">{stats.participants}</StatNumber>
              </Stat>
              <Stat minW="120px">
                <StatLabel fontSize="xs">A enviar</StatLabel>
                <StatNumber fontSize="2xl">{stats.recipients}</StatNumber>
              </Stat>
              <Stat minW="120px">
                <StatLabel fontSize="xs">Já receberam</StatLabel>
                <StatNumber fontSize="2xl">{stats.already_sent}</StatNumber>
              </Stat>
            </Flex>

            {stats.missing_email > 0 && (
              <Alert status="warning" borderRadius="md" mt={4} fontSize="sm">
                <AlertIcon />
                {stats.missing_email} participante(s) sem e-mail cadastrado. Eles não receberão o certificado — preencha
                o e-mail e envie novamente.
              </Alert>
            )}

            {stats.recipients === 0 && (
              <Alert status="info" borderRadius="md" mt={4} fontSize="sm">
                <AlertIcon />
                Nenhum destinatário pendente.
                {stats.already_sent > 0 && ' Marque "reenviar" abaixo para enviar de novo a quem já recebeu.'}
              </Alert>
            )}
          </>
        )}

        <Divider my={4} />

        <Flex direction="column" gap={4}>
          <FormField
            label="Data do certificado"
            helperText='Impressa no certificado, após "São Paulo,".'
            error={formErrors.certificate_date}
            isRequired
            maxW="320px"
          >
            <Input
              type="date"
              value={certificateDate}
              onChange={(e) => setCertificateDate(e.target.value)}
              isDisabled={sending}
            />
          </FormField>

          <FormField label="Assunto do e-mail" error={formErrors.subject} isRequired>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={255} isDisabled={sending} />
          </FormField>

          <FormField
            label="Corpo do e-mail"
            helperText={
              <Box as="span">
                Variáveis disponíveis: <strong>#PRIMEIRO_NOME</strong> (primeiro nome do participante) e{' '}
                <strong>#NOME_EVENTO</strong> (nome do concurso). O certificado vai anexado ao e-mail.
              </Box>
            }
            error={formErrors.body}
            isRequired
          >
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              maxLength={10000}
              isDisabled={sending}
            />
          </FormField>
        </Flex>
      </FormModal>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        onConfirm={handleSend}
        action="send"
        title="Confirmar envio de certificados"
        description={`Deseja enviar o certificado de expositor para ${stats?.recipients ?? 0} participante(s)${
          force ? ', INCLUINDO quem já recebeu' : ''
        }? Esta ação não pode ser desfeita.`}
        confirmLabel="Enviar"
        isLoading={sending}
      />
    </>
  )
}
